import React from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DescriptionIcon from "@mui/icons-material/Description";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <PsychologyIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "Powerful resume builder",
    desc: "Use our potent creation tools and expert guidance to create the perfect resume for your next job application.",
    color: "linear-gradient(45deg, #FF9A9E 0%, #FAD0C4 100%)",
  },
  {
    icon: <DescriptionIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "Professional templates",
    desc: "Choose from 25+ applicant tracking systems (ATS)-friendly modern and professional templates.",
    color: "linear-gradient(45deg, #A1C4FD 0%, #C2E9FB 100%)",
  },
  {
    icon: <ColorLensIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "Customize fonts and colors",
    desc: "Select custom fonts and colors on any resume template.",
    color: "linear-gradient(45deg, #FFECD2 0%, #FCB69F 100%)",
  },
  {
    icon: <DescriptionIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "Free resume examples",
    desc: "Use our more than 500 resume examples and templates to see what a great resume looks like in practice.",
    color: "linear-gradient(45deg, #84FAB0 0%, #8FD3F4 100%)",
  },
  {
    icon: <AssignmentTurnedInIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "ATS-friendly templates",
    desc: "Sail through applicant tracking systems with resume templates that appeal to both machines and humans.",
    color: "linear-gradient(45deg, #FFC3A0 0%, #FFAFBD 100%)",
  },
  {
    icon: <TipsAndUpdatesIcon sx={{ fontSize: { xs: 44, sm: 50 } }} />,
    title: "Expert tips and guidance",
    desc: "Get help every step of the way as you build your resume with expert tips and suggested phrases.",
    color: "linear-gradient(45deg, #D4FC79 0%, #96E6A1 100%)",
  },
];

const FeaturePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  return (
    <Box
      component="section"
      aria-labelledby="features-heading"
      itemScope
      itemType="https://schema.org/ItemList"
      sx={{
        textAlign: "center",
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 },
        background: "#fff",
      }}
    >
      <Typography
        id="features-heading"
        component="h2"
        fontWeight="bold"
        gutterBottom
        itemProp="name"
        sx={{
          mb: { xs: 1.5, sm: 2, md: 4 },
          fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.4rem" },
          lineHeight: 1.25,
          background: "linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Get hired 36% faster with our feature-packed resume builder
      </Typography>

      <Typography
        component="p"
        sx={{
          mb: { xs: 3, sm: 4, md: 6 },
          color: theme.palette.text.secondary,
          maxWidth: 700,
          mx: "auto",
          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
          lineHeight: 1.6,
        }}
      >
        Create a professional resume that stands out with our powerful tools
        and beautiful templates
      </Typography>

      {/*
       * GRID LAYOUT — exactly matching original screenshot 3:
       *   Desktop (md+):  3 columns, all equal
       *   Tablet (sm):    2 columns
       *   Mobile (xs):    1 column, full width — matching screenshot 1
       */}
      <Grid
        container
        spacing={isMobile ? 2 : 4}
        justifyContent="center"
        sx={{ px: { xs: 0, sm: 2, md: 4 } }}
        component="ol"
        aria-label="Resume builder features"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        {features.map((feature, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={index}
            component="li"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <meta itemProp="position" content={String(index + 1)} />
            <Card
              itemScope
              itemType="https://schema.org/Service"
              aria-label={feature.title}
              sx={{
                /*
                 * Desktop: fixed height like original
                 * Mobile: auto height so text never clips
                 */
                height: { xs: "auto", sm: 270, md: 280 },
                minHeight: { xs: 190, sm: 270 },
                width: "100%",
                maxWidth: { xs: "100%", sm: 400 },
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                p: { xs: 2.5, sm: 3 },
                borderRadius: 4,
                boxShadow: "0 8px 16px rgba(0,0,0,0.10)",
                background: feature.color,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 14px 22px rgba(0,0,0,0.14)",
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 68, sm: 80 },
                  height: { xs: 68, sm: 80 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.22)",
                  borderRadius: "50%",
                  mb: { xs: 1.5, sm: 2 },
                }}
                aria-hidden="true"
              >
                {feature.icon}
              </Box>
              <CardContent sx={{ p: "0 !important" }}>
                <Typography
                  component="h3"
                  gutterBottom
                  itemProp="name"
                  sx={{
                    fontWeight: "bold",
                    color: "common.black",
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                    lineHeight: 1.3,
                    mb: { xs: 0.75, sm: 1 },
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  itemProp="description"
                  sx={{
                    color: "rgba(0,0,0,0.78)",
                    fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                    lineHeight: 1.55,
                  }}
                >
                  {feature.desc}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* CTA */}
      <Button
        variant="contained"
        size="large"
        onClick={() => navigate("/steps")}
        aria-label="Get Started Now – build your professional resume"
        sx={{
          background: "linear-gradient(to right, #6a11cb, #2575fc)",
          color: "#fff",
          fontWeight: "bold",
          px: { xs: 4, sm: 5 },
          py: { xs: 1.3, sm: 1.5 },
          mt: { xs: 4, sm: 5 },
          borderRadius: "12px",
          fontSize: { xs: "0.95rem", sm: "1rem" },
          textTransform: "none",
          boxShadow: "0 6px 16px rgba(106,17,203,0.3)",
          "&:hover": {
            background: "linear-gradient(to right, #2575fc, #6a11cb)",
            boxShadow: "0 10px 20px rgba(106,17,203,0.4)",
          },
        }}
      >
        Get Started Now
      </Button>
    </Box>
  );
};

export default FeaturePage;